import React, { useEffect, useState } from "react";
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import LightModeIcon from '@mui/icons-material/LightMode';
import List from '@mui/material/List';
import ListIcon from '@mui/icons-material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import DescriptionIcon from '@mui/icons-material/Description';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import Tooltip from '@mui/material/Tooltip';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import resumePdf from '../assets/resume.pdf';

const drawerWidth = 240;
const navItems = [['Expertise', 'expertise'], ['Certifications', 'certifications'], ['History', 'history'], ['Projects', 'projects'], ['Contact', 'contact']];
const emailAddress = "rohannair1099@yahoo.com";
const phoneNumber = "6023267270";

// Define the props interface
interface NavigationProps {
  parentToChild: { mode: string };
  modeChange: () => void;
}

function Navigation({ parentToChild, modeChange }: NavigationProps) {
  const { mode } = parentToChild;

  const [mobileOpen, setMobileOpen] = useState<boolean>(false);
  const [scrolled, setScrolled] = useState<boolean>(false);
  const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);
  const [isMaximized, setIsMaximized] = useState<boolean>(false);

  const handleDrawerToggle = () => {
    setMobileOpen((prevState) => !prevState);
  };

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
    if (isPopupOpen) setIsMaximized(false); // Reset maximized state when closing
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  useEffect(() => {
    const handleScroll = () => {
      const navbar = document.getElementById("navigation");
      if (navbar) {
        const scrolled = window.scrollY > navbar.clientHeight;
        setScrolled(scrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToSection = (section: string) => {
    const element = document.getElementById(section);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      console.error(`Element with id "${section}" not found`);
    }
  };

  const drawer = (
    <Box className="navigation-bar-responsive" onClick={handleDrawerToggle} sx={{ textAlign: 'center' }}>
      <p className="mobile-menu-top"><ListIcon />Menu</p>
      <Divider />
      <List>
        {navItems.map((item) => (
          <ListItem key={item[0]} disablePadding>
            <ListItemButton sx={{ textAlign: 'center' }} onClick={() => scrollToSection(item[1])}>
              <ListItemText primary={item[0]} />
            </ListItemButton>
          </ListItem>
        ))}
        {/* Resume button in drawer */}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} onClick={togglePopup}>
            <ListItemText primary="Resume" />
          </ListItemButton>
        </ListItem>
        {/* Email in drawer */}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} component="a" href={`mailto:${emailAddress}`}>
            <ListItemText primary="Email" secondary={emailAddress} />
          </ListItemButton>
        </ListItem>
        {/* Phone in drawer */}
        <ListItem disablePadding>
          <ListItemButton sx={{ textAlign: 'center' }} component="a" href={`tel:${phoneNumber}`}>
            <ListItemText primary="Phone" secondary={phoneNumber} />
          </ListItemButton>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar component="nav" id="navigation" className={`navbar-fixed-top${scrolled ? ' scrolled' : ''}`}>
        <Toolbar className='navigation-bar'>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          {/* Contact info and mode toggle container */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {mode === 'dark' ? (
              <LightModeIcon onClick={() => modeChange()} />
            ) : (
              <DarkModeIcon onClick={() => modeChange()} />
            )}
            
            {/* Contact information with icons */}
            <Box sx={{ 
              display: { xs: 'none', md: 'flex' }, 
              alignItems: 'center', 
              gap: '15px',
              marginLeft: '10px'
            }}>
              {/* Email with tooltip */}
              <Tooltip title={emailAddress}>
                <IconButton component="a" href={`mailto:${emailAddress}`} sx={{ color: '#fff' }}>
                  <EmailIcon />
                </IconButton>
              </Tooltip>
              
              {/* Phone with tooltip */}
              <Tooltip title={phoneNumber}>
                <IconButton component="a" href={`tel:${phoneNumber}`} sx={{ color: '#fff' }}>
                  <PhoneIcon />
                </IconButton>
              </Tooltip>
            </Box>
            
            {/* Resume Button with icon */}
            <Button 
              onClick={togglePopup}
              sx={{ 
                color: '#fff', 
                display: 'flex', 
                alignItems: 'center', 
                gap: '5px',
                marginLeft: '10px'
              }}
            >
              <DescriptionIcon sx={{ fontSize: '1.2rem' }} />
              Resume
            </Button>
          </Box>
          
          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {navItems.map((item) => (
              <Button key={item[0]} onClick={() => scrollToSection(item[1])} sx={{ color: '#fff' }}>
                {item[0]}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      <nav>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawer}
        </Drawer>
      </nav>

      {/* Popup for Resume */}
      {isPopupOpen && (
        <Box className="resume-popup-overlay">
          <Box className={`resume-popup ${isMaximized ? 'maximized' : ''}`}>
            <Box className="popup-header">
              <IconButton onClick={togglePopup}>
                <CloseIcon />
              </IconButton>
              <IconButton onClick={toggleMaximize}>
                {isMaximized ? <FullscreenExitIcon /> : <FullscreenIcon />}
              </IconButton>
            </Box>
            <Box className="popup-content">
              <embed src={resumePdf} type="application/pdf" width="100%" height="100%" />
            </Box>
            <Box className="popup-footer">
              <Button
                component="a"
                href={resumePdf}
                download="Rohan_Nair_Resume.pdf"
                sx={{ display: 'flex', alignItems: 'center', gap: '5px' }}
              >
                <DownloadIcon />
                Download
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
}

export default Navigation;